import { createSlice } from '@reduxjs/toolkit';
import {
    getFavoriteProductTagsByFavoriteProductIdAction,
    getFavoriteProductTagsByFavoriteProductIdsAction,
    createFavoriteProductTagAction,
    deleteFavoriteProductTagAction,
} from '../../actions/favoriteProductTagActions';
import { Tag } from '../../actions/tagActions/types';

export interface FavoriteProductTag {
    id: number;
    favoriteProductId: number;
    tagId: number;
    tag: Tag;
}

export interface FavoriteProductTagState {
    byFavoriteProductId: Record<
        number,
        { pending: boolean; tags: FavoriteProductTag[]; fetchedAt?: number }
    >;
    pending: boolean;
}

const initialState: FavoriteProductTagState = {
    byFavoriteProductId: {},
    pending: false,
};

export const favoriteProductTagSlice = createSlice({
    name: 'favoriteProductTags',
    initialState,
    reducers: {
        clearFavoriteProductTagsForFavoriteProduct: (state, action) => {
            delete state.byFavoriteProductId[action.payload];
        },
        addFavoriteProductTagsOptimistic: (state, action) => {
            const { favoriteProductId, tags } = action.payload;
            if (!state.byFavoriteProductId[favoriteProductId]) {
                state.byFavoriteProductId[favoriteProductId] = { pending: false, tags: [] };
            }
            const entry = state.byFavoriteProductId[favoriteProductId];
            for (const tag of tags) {
                const exists = entry.tags.some((t) => t.tagId === tag.tagId || t.id === tag.id);
                if (!exists) {
                    entry.tags.push(tag);
                }
            }
        },
        removeFavoriteProductTagsByTagId: (state, action) => {
            const { favoriteProductId, tagIds } = action.payload;
            const entry = state.byFavoriteProductId[favoriteProductId];
            if (!entry) {
                return;
            }
            entry.tags = entry.tags.filter((t) => !tagIds.includes(t.id));
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getFavoriteProductTagsByFavoriteProductIdAction.pending, (state, action) => {
                const productId = action.meta.arg;
                state.byFavoriteProductId[productId] = { pending: true, tags: [] };
            })
            .addCase(getFavoriteProductTagsByFavoriteProductIdAction.fulfilled, (state, action) => {
                const { favoriteProductId, tags } = action.payload;
                state.byFavoriteProductId[favoriteProductId] = {
                    pending: false,
                    tags,
                    fetchedAt: Date.now(),
                };
            })
            .addCase(getFavoriteProductTagsByFavoriteProductIdAction.rejected, (state, action) => {
                const favoriteProductId = action.meta.arg;
                state.byFavoriteProductId[favoriteProductId] = { pending: false, tags: [] };
            })
            .addCase(createFavoriteProductTagAction.fulfilled, (state, action) => {
                const favoriteProductId = action.payload?.favoriteProductId;
                const tag = action.payload?.created;
                if (!favoriteProductId || !tag) {
                    return;
                }

                if (state.byFavoriteProductId[favoriteProductId]) {
                    const entry = state.byFavoriteProductId[favoriteProductId];
                    const idx = entry.tags.findIndex(
                        (t) => t.tagId === tag.tagId || t.id === tag.id,
                    );
                    if (idx === -1) {
                        entry.tags.push(tag);
                    } else {
                        entry.tags[idx] = tag;
                    }
                } else {
                    state.byFavoriteProductId[favoriteProductId] = { pending: false, tags: [tag] };
                }
            })
            .addCase(deleteFavoriteProductTagAction.fulfilled, (state, action) => {
                const id = action.payload?.id;
                for (const key of Object.keys(state.byFavoriteProductId)) {
                    const pid = Number.parseInt(key, 10);
                    const entry = state.byFavoriteProductId[pid];
                    if (!entry) {
                        continue;
                    }
                    entry.tags = entry.tags.filter((t) => t.id !== id);
                }
            })
            .addCase(
                getFavoriteProductTagsByFavoriteProductIdsAction.fulfilled,
                (state, action) => {
                    const allTags: FavoriteProductTag[] = action.payload ?? [];
                    const grouped: Record<number, FavoriteProductTag[]> = {};
                    for (const tag of allTags) {
                        const pid = tag.favoriteProductId;
                        if (!grouped[pid]) {
                            grouped[pid] = [];
                        }
                        grouped[pid].push(tag);
                    }
                    for (const [key, tags] of Object.entries(grouped)) {
                        state.byFavoriteProductId[Number(key)] = {
                            pending: false,
                            tags,
                            fetchedAt: Date.now(),
                        };
                    }
                },
            );
    },
});

export default favoriteProductTagSlice.reducer;
export const {
    addFavoriteProductTagsOptimistic,
    removeFavoriteProductTagsByTagId,
    clearFavoriteProductTagsForFavoriteProduct,
} = favoriteProductTagSlice.actions;
